import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Users, Car, Clock, Gauge, AlertTriangle, CalendarClock, Loader2, CheckCircle2 } from 'lucide-react';
import api from '../lib/api';
import { RenewLeaseDialog } from '../components/leasing/RenewLeaseDialog';
import { UnassignedLesseesCard } from '../components/leasing/ReassignLessee';
import { useAuth } from '../context/AuthContext';
import { MaintenanceCostsCard } from '../components/leasing/MaintenanceCostsCard';
import { useLanguage } from '../context/LanguageContext';

const StatTile = ({ icon: Icon, label, value, testId, to }) => (
    <Link to={to} className="fleet-card p-5 hover:shadow-md transition-shadow" data-testid={testId}>
        <div className="w-10 h-10 bg-[#1B7EB5]/10 rounded-sm flex items-center justify-center mb-3">
            <Icon className="w-5 h-5 text-[#1B7EB5]" />
        </div>
        <p className="text-3xl font-bold text-[#1B7EB5]">{value}</p>
        <p className="text-xs uppercase tracking-wide text-slate-500 mt-1">{label}</p>
    </Link>
);

const alertLabel = (a, el) => {
    if (a.type === 'contract_end') {
        if (a.days_left < 0) return el ? `Η σύμβαση έληξε πριν ${-a.days_left} ημέρες` : `Contract expired ${-a.days_left} days ago`;
        return el ? `Λήξη σύμβασης σε ${a.days_left} ημέρες` : `Contract ends in ${a.days_left} days`;
    }
    return a.level === '100'
        ? (el ? `Υπέρβαση ορίου km (${a.percent}%)` : `Km limit exceeded (${a.percent}%)`)
        : (el ? `${a.percent}% του ορίου km` : `${a.percent}% of km limit`);
};

const AlertRow = ({ a, el, onRenewed }) => {
    const critical = a.severity === 'critical';
    const Icon = a.type === 'contract_end' ? CalendarClock : Gauge;
    return (
        <div className={`flex items-center gap-3 p-3 rounded-sm border ${critical ? 'border-red-200 bg-red-50' : 'border-amber-200 bg-amber-50'}`} data-testid={`leasing-alert-${a.leasing_vehicle_id}-${a.type}`}>
            <Icon className={`w-5 h-5 ${critical ? 'text-red-600' : 'text-amber-600'}`} />
            <div className="flex-1">
                <p className="font-semibold text-slate-800">{a.vehicle} ({a.license_plate})</p>
                <p className="text-xs text-slate-500">{el ? 'Μισθωτής' : 'Lessee'}: {a.client_company_name}</p>
            </div>
            <span className={`text-sm font-medium ${critical ? 'text-red-700' : 'text-amber-700'}`}>{alertLabel(a, el)}</span>
            <RenewLeaseDialog alert={a} el={el} onRenewed={onRenewed} />
        </div>
    );
};

export default function LeasingDashboardPage() {
    const { language } = useLanguage();
    const el = language === 'el';
    const { user } = useAuth();
    const [data, setData] = useState(null);

    const load = useCallback(() => {
        api.get('/leasing/dashboard').then((res) => setData(res.data)).catch(() => setData({ lessees: 0, vehicles: 0, pending: 0, total_km: 0, alerts: [] }));
    }, []);

    useEffect(() => { load(); }, [load]);

    if (!data) return <Loader2 className="w-6 h-6 animate-spin text-[#1B7EB5]" />;

    return (
        <div className="space-y-6" data-testid="leasing-dashboard">
            <div>
                <h1 className="text-2xl font-bold text-slate-800">{el ? 'Πίνακας Leasing' : 'Leasing Dashboard'}</h1>
                <p className="text-zinc-500">{el ? 'Επισκόπηση μισθωτών και οχημάτων' : 'Overview of lessees and vehicles'}</p>
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <StatTile icon={Users} label={el ? 'Μισθωτές' : 'Lessees'} value={data.lessees} testId="leasing-stat-lessees" to="/app/leasing-clients" />
                <StatTile icon={Car} label={el ? 'Οχήματα' : 'Vehicles'} value={data.vehicles} testId="leasing-stat-vehicles" to="/app/leasing-vehicles" />
                <StatTile icon={Clock} label={el ? 'Εκκρεμείς επιβεβαιώσεις' : 'Pending confirmations'} value={data.pending} testId="leasing-stat-pending" to="/app/leasing-vehicles" />
                <StatTile icon={Gauge} label={el ? 'Σύνολο km' : 'Total km'} value={data.total_km.toLocaleString(el ? 'el-GR' : 'en-GB', { maximumFractionDigits: 1 })} testId="leasing-stat-km" to="/app/leasing-clients" />
            </div>
            {user?.role === 'admin' && <UnassignedLesseesCard el={el} />}
            <MaintenanceCostsCard el={el} />
            <div className="fleet-card p-5 space-y-3" data-testid="leasing-alerts">
                <div className="flex items-center gap-2">
                    <AlertTriangle className="w-5 h-5 text-amber-600" />
                    <h2 className="text-base md:text-lg font-semibold text-slate-800">{el ? 'Λήξεις & όρια χιλιομέτρων' : 'Expirations & km limits'} ({data.alerts.length})</h2>
                </div>
                {data.alerts.length ? data.alerts.map((a) => <AlertRow key={`${a.leasing_vehicle_id}-${a.type}`} a={a} el={el} onRenewed={load} />) : (
                    <p className="text-sm text-slate-500 flex items-center gap-2" data-testid="leasing-alerts-empty">
                        <CheckCircle2 className="w-4 h-4 text-green-600" />
                        {el ? 'Καμία σύμβαση δεν λήγει τις επόμενες 30 ημέρες και κανένα όχημα δεν πλησιάζει το όριο km.' : 'No contracts ending in the next 30 days and no vehicle near its km limit.'}
                    </p>
                )}
            </div>
        </div>
    );
}
